import { useEffect } from 'react'
import { CANONICAL_DOMAIN } from '@/data/seoArchitecture'

export interface SeoHeadProps {
  title: string
  description: string
  canonicalPath: string
  ogType?: 'website' | 'article'
  ogImage?: string
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>
  noindex?: boolean
  isWhiteLabel?: boolean
  brandName?: string | null
}

function upsertMetaTag(
  selector: string,
  attributes: Record<string, string>,
  content: string
): void {
  if (typeof document === 'undefined') return
  let element = document.head.querySelector<HTMLMetaElement>(selector)
  if (!element) {
    element = document.createElement('meta')
    Object.entries(attributes).forEach(([key, value]) => {
      element?.setAttribute(key, value)
    })
    document.head.appendChild(element)
  }
  element.setAttribute('content', content)
}

function upsertCanonicalLink(href: string): void {
  if (typeof document === 'undefined') return
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!link) {
    link = document.createElement('link')
    link.setAttribute('rel', 'canonical')
    document.head.appendChild(link)
  }
  link.setAttribute('href', href)
}

function safeJsonLdSerialize(payload: unknown): string {
  return JSON.stringify(payload)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
}

export function SeoHead({
  title,
  description,
  canonicalPath,
  ogType = 'website',
  ogImage = `${CANONICAL_DOMAIN}/logo-light.png`,
  jsonLd,
  noindex = false,
  isWhiteLabel = false,
  brandName,
}: SeoHeadProps) {
  useEffect(() => {
    if (typeof document === 'undefined') return

    const effectiveTitle =
      isWhiteLabel && brandName ? `${brandName} — Portal Financeiro` : title
    const normalizedPath = canonicalPath.startsWith('/')
      ? canonicalPath
      : `/${canonicalPath}`
    const canonicalUrl = `${CANONICAL_DOMAIN}${normalizedPath === '/' ? '/' : normalizedPath}`

    document.title = effectiveTitle

    upsertMetaTag('meta[name="description"]', { name: 'description' }, description)

    const shouldNoIndex = noindex || (isWhiteLabel && normalizedPath !== '/')
    upsertMetaTag(
      'meta[name="robots"]',
      { name: 'robots' },
      shouldNoIndex
        ? 'noindex, nofollow'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
    )

    if (!isWhiteLabel) {
      upsertCanonicalLink(canonicalUrl)
    }

    upsertMetaTag('meta[property="og:type"]', { property: 'og:type' }, ogType)
    upsertMetaTag('meta[property="og:locale"]', { property: 'og:locale' }, 'pt_BR')
    upsertMetaTag(
      'meta[property="og:site_name"]',
      { property: 'og:site_name' },
      isWhiteLabel && brandName ? brandName : 'Seven Analytics'
    )
    upsertMetaTag('meta[property="og:title"]', { property: 'og:title' }, effectiveTitle)
    upsertMetaTag(
      'meta[property="og:description"]',
      { property: 'og:description' },
      description
    )
    upsertMetaTag('meta[property="og:url"]', { property: 'og:url' }, canonicalUrl)
    upsertMetaTag('meta[property="og:image"]', { property: 'og:image' }, ogImage)
    upsertMetaTag(
      'meta[property="og:image:secure_url"]',
      { property: 'og:image:secure_url' },
      ogImage
    )
    upsertMetaTag('meta[property="og:image:type"]', { property: 'og:image:type' }, 'image/png')
    upsertMetaTag('meta[property="og:image:width"]', { property: 'og:image:width' }, '1200')
    upsertMetaTag('meta[property="og:image:height"]', { property: 'og:image:height' }, '630')
    upsertMetaTag(
      'meta[property="og:image:alt"]',
      { property: 'og:image:alt' },
      effectiveTitle
    )

    upsertMetaTag(
      'meta[name="twitter:card"]',
      { name: 'twitter:card' },
      'summary_large_image'
    )
    upsertMetaTag(
      'meta[name="twitter:title"]',
      { name: 'twitter:title' },
      effectiveTitle
    )
    upsertMetaTag(
      'meta[name="twitter:description"]',
      { name: 'twitter:description' },
      description
    )
    upsertMetaTag('meta[name="twitter:image"]', { name: 'twitter:image' }, ogImage)

    const existingScript = document.getElementById('seven-jsonld-schema')
    if (isWhiteLabel || !jsonLd) {
      if (existingScript) {
        existingScript.remove()
      }
      return
    }

    let scriptEl = existingScript as HTMLScriptElement | null
    if (!scriptEl) {
      scriptEl = document.createElement('script')
      scriptEl.id = 'seven-jsonld-schema'
      scriptEl.type = 'application/ld+json'
      document.head.appendChild(scriptEl)
    }
    scriptEl.textContent = safeJsonLdSerialize(jsonLd)
  }, [
    title,
    description,
    canonicalPath,
    ogType,
    ogImage,
    jsonLd,
    noindex,
    isWhiteLabel,
    brandName,
  ])

  return null
}
